import React from "react";

/**
 * Lightweight markdown rendering for Copilot messages, forked from the
 * AIAgentPanel renderer and adapted: internal paths navigate via onNavigate,
 * external URLs open in a new tab, and fenced code blocks are handled —
 * ```html fences render as a sanitized email-preview card (the LLM proposes
 * email bodies as HTML), other fences render as code. Streaming-safe: an
 * unclosed trailing fence renders as an in-progress block.
 */

// Whitelist serializer: rebuilds HTML keeping only safe tags and href on
// links. Everything else (attributes, scripts, unknown tags) is dropped
// while its inner text survives.
const ALLOWED_TAGS = new Set([
  "P", "BR", "A", "STRONG", "B", "EM", "I", "U", "UL", "OL", "LI",
  "DIV", "SPAN", "H1", "H2", "H3", "H4", "BLOCKQUOTE",
  "TABLE", "THEAD", "TBODY", "TR", "TD", "TH", "HR"
]);

const escapeHtml = (value = "") =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const serializeSafe = node => {
  let out = "";
  node.childNodes.forEach(child => {
    if (child.nodeType === Node.TEXT_NODE) {
      out += escapeHtml(child.textContent);
      return;
    }
    if (child.nodeType !== Node.ELEMENT_NODE) return;
    const tag = child.tagName;
    const lower = tag.toLowerCase();
    const inner = serializeSafe(child);
    if (!ALLOWED_TAGS.has(tag)) {
      out += inner;
      return;
    }
    if (lower === "br" || lower === "hr") {
      out += `<${lower}/>`;
      return;
    }
    if (lower === "a") {
      const href = child.getAttribute("href") || "";
      const safeHref = /^(https?:|mailto:)/i.test(href) ? href : "#";
      out += `<a href="${escapeHtml(safeHref)}" target="_blank" rel="noopener noreferrer">${inner}</a>`;
      return;
    }
    out += `<${lower}>${inner}</${lower}>`;
  });
  return out;
};

export const sanitizeHtml = html => {
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    return serializeSafe(doc.body);
  } catch (e) {
    return escapeHtml(html);
  }
};

// Bold and inline code. Backticks used to survive as literal characters.
const parseBold = text => {
  const parts = text.split(/(\*\*.*?\*\*|`[^`\n]+`)/g);
  return parts.map((part, index) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return <strong key={index}>{part.slice(2, -2)}</strong>;
    }
    if (part.length > 2 && part.startsWith("`") && part.endsWith("`")) {
      return (
        <code className="copilotMd__code" key={index}>
          {part.slice(1, -1)}
        </code>
      );
    }
    return part;
  });
};

const parseInline = (text, onNavigate) => {
  if (!text) return null;
  // Bold wrapped around a link ("**[CC Testing](/sequences/1)**") used to
  // survive as literal asterisks: the link split runs first and strands the
  // ** in sibling parts, where parseBold can no longer pair them. Links
  // already carry their own emphasis, so drop the markers.
  const source = text
    .replace(/\*\*(\[[^\]]*\]\([^)]*\))\*\*/g, "$1")
    .replace(/__(\[[^\]]*\]\([^)]*\))__/g, "$1");
  const parts = source.split(/(\[.*?\]\(.*?\))/g);

  return parts.map((part, index) => {
    const linkMatch = part.match(/\[(.*?)\]\((.*?)\)/);
    if (linkMatch) {
      const label = linkMatch[1];
      const url = linkMatch[2];
      const isExternal = /^https?:\/\//i.test(url);

      if (isExternal) {
        return (
          <a
            key={index}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="copilotLink"
          >
            {label}
            <span className="material-icons-outlined copilotLink__extIcon">
              open_in_new
            </span>
          </a>
        );
      }

      return (
        <span
          key={index}
          className="copilotLink"
          onClick={() => onNavigate && onNavigate(url)}
        >
          {label}
        </span>
      );
    }
    return <span key={index}>{parseBold(part)}</span>;
  });
};

const renderTextSegment = (text, onNavigate, keyPrefix) => {
  if (!text) return [];
  const lines = text.split("\n");
  const blocks = [];
  let bullets = [];
  let key = 0;

  // Bullets and numbered items share one buffer: only the list tag differs, and
  // a run is never both.
  let ordered = false;

  // Pipe tables arrive as a run of consecutive lines, so they need the same
  // buffer treatment as bullets. Without this the model's "| Channel | Sent |"
  // blocks reached the bubble as literal pipes and "|---|---|" separators.
  let rows = [];

  const isSeparatorRow = cells => cells.every(c => /^:?-{2,}:?$/.test(c.trim()));

  const flushTable = () => {
    if (!rows.length) return;
    const body = rows.slice(1).filter(cells => !isSeparatorRow(cells));
    const head = rows[0];
    blocks.push(
      <div className="copilotMd__tableWrap" key={`${keyPrefix}-tw-${key++}`}>
        <table className="copilotMd__table">
          <thead>
            <tr>
              {head.map((cell, i) => (
                <th key={i}>{parseInline(cell, onNavigate)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {body.map((cells, r) => (
              <tr key={r}>
                {cells.map((cell, i) => (
                  <td key={i}>{parseInline(cell, onNavigate)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
    rows = [];
  };

  const flushBullets = () => {
    if (bullets.length) {
      const ListTag = ordered ? "ol" : "ul";
      blocks.push(
        <ListTag className="copilotMd__list" key={`${keyPrefix}-ul-${key++}`}>
          {bullets.map((item, i) => (
            <li key={i}>{parseInline(item, onNavigate)}</li>
          ))}
        </ListTag>
      );
      bullets = [];
    }
  };

  lines.forEach(line => {
    // The model routinely answers with ### headings and 1. lists; without
    // these the markers rendered as literal text in the bubble.
    // A table row is any line fenced by pipes. Buffer it; the run is emitted
    // once a non-row line (or the end of the segment) closes it.
    if (/^\s*\|.*\|\s*$/.test(line)) {
      flushBullets();
      rows.push(line.trim().replace(/^\||\|$/g, "").split("|"));
      return;
    }

    const headingMatch = line.match(/^\s*(#{1,4})\s+(.*)$/);
    if (headingMatch) {
      flushTable();
      flushBullets();
      // Two sizes only. A chat bubble has no room for six levels, and ### —
      // what the model emits most — must not end up the smallest thing on
      // screen.
      const HeadingTag = headingMatch[1].length <= 2 ? "h3" : "h4";
      blocks.push(
        <HeadingTag className="copilotMd__heading" key={`${keyPrefix}-h-${key++}`}>
          {parseInline(headingMatch[2], onNavigate)}
        </HeadingTag>
      );
      return;
    }
    const bulletMatch = line.match(/^\s*[*-]\s+(.*)$/);
    if (bulletMatch) {
      flushTable();
      if (ordered) flushBullets();
      ordered = false;
      bullets.push(bulletMatch[1]);
      return;
    }
    const numberedMatch = line.match(/^\s*\d+[.)]\s+(.*)$/);
    if (numberedMatch) {
      flushTable();
      if (!ordered) flushBullets();
      ordered = true;
      bullets.push(numberedMatch[1]);
      return;
    }
    flushTable();
    flushBullets();
    if (line.trim() === "") {
      blocks.push(<div className="copilotMd__break" key={`${keyPrefix}-b-${key++}`} />);
    } else {
      blocks.push(
        <div className="copilotMd__line" key={`${keyPrefix}-l-${key++}`}>
          {parseInline(line, onNavigate)}
        </div>
      );
    }
  });
  flushTable();
  flushBullets();
  return blocks;
};

const renderFence = (raw, keyPrefix) => {
  const newlineAt = raw.indexOf("\n");
  const lang = (newlineAt >= 0 ? raw.slice(0, newlineAt) : raw).trim().toLowerCase();
  const content = newlineAt >= 0 ? raw.slice(newlineAt + 1) : "";
  const looksLikeHtml = lang === "html" || content.trim().startsWith("<");

  if (looksLikeHtml) {
    return (
      <div
        className="copilotEmailPreview"
        key={`${keyPrefix}-html`}
        // sanitized above via whitelist serializer — no attrs/scripts survive
        dangerouslySetInnerHTML={{ __html: sanitizeHtml(content) }}
      />
    );
  }
  return (
    <pre className="copilotMd__code" key={`${keyPrefix}-code`}>
      {content || raw}
    </pre>
  );
};

// The model sometimes writes an email body as bare HTML outside a fence; those
// tags rendered as literal text. Find that span so it gets the same preview card.
const BLOCK_TAGS = "p|div|ul|ol|table|blockquote|h[1-4]";
const splitBareHtml = segment => {
  const start = segment.search(new RegExp(`<(${BLOCK_TAGS})[\\s>]`, "i"));
  if (start < 0) return null;
  const last = [...segment.matchAll(new RegExp(`</(${BLOCK_TAGS})>`, "gi"))].pop();
  if (!last || last.index < start) return null;
  const end = last.index + last[0].length;
  return [segment.slice(0, start), segment.slice(start, end), segment.slice(end)];
};

export const renderMarkdown = (text, onNavigate) => {
  if (!text) return null;
  // Split on fences; even indices are prose, odd are fence contents. A
  // trailing unclosed fence (mid-stream) lands as a final odd segment.
  const segments = text.split("```");
  const out = [];
  segments.forEach((segment, index) => {
    const html = index % 2 === 0 ? splitBareHtml(segment) : null;
    if (html) {
      out.push(...renderTextSegment(html[0], onNavigate, `s${index}a`));
      out.push(renderFence(`html\n${html[1]}`, `s${index}`));
      out.push(...renderTextSegment(html[2], onNavigate, `s${index}z`));
    } else if (index % 2 === 0) {
      out.push(...renderTextSegment(segment, onNavigate, `s${index}`));
    } else {
      out.push(renderFence(segment, `s${index}`));
    }
  });
  return out;
};

export default renderMarkdown;
